import { create } from 'zustand';

type Theme = 'light' | 'dark' | 'system';
type AccentColor = 'blue' | 'violet' | 'emerald' | 'rose' | 'amber' | 'cyan';

interface ThemeState {
  theme: Theme;
  accentColor: AccentColor;
  setTheme: (theme: Theme) => void;
  setAccentColor: (color: AccentColor) => void;
  toggleTheme: () => void;
}

/* ─── Accent color CSS variable presets (HSL values) ──────────────── */
const ACCENT_PRESETS: Record<AccentColor, { light: Record<string, string>; dark: Record<string, string> }> = {
  blue: {
    light: { '--primary': '221.2 83.2% 53.3%', '--primary-foreground': '210 40% 98%', '--ring': '221.2 83.2% 53.3%' },
    dark:  { '--primary': '217.2 91.2% 59.8%', '--primary-foreground': '222.2 47.4% 11.2%', '--ring': '224.3 76.3% 48%' },
  },
  violet: {
    light: { '--primary': '262.1 83.3% 57.8%', '--primary-foreground': '210 40% 98%', '--ring': '262.1 83.3% 57.8%' },
    dark:  { '--primary': '263.4 70% 50.4%', '--primary-foreground': '210 40% 98%', '--ring': '263.4 70% 50.4%' },
  },
  emerald: {
    light: { '--primary': '160.1 84.1% 39.4%', '--primary-foreground': '210 40% 98%', '--ring': '160.1 84.1% 39.4%' },
    dark:  { '--primary': '160 84% 39%', '--primary-foreground': '210 40% 98%', '--ring': '160 84% 39%' },
  },
  rose: {
    light: { '--primary': '346.8 77.2% 49.8%', '--primary-foreground': '355.7 100% 97.3%', '--ring': '346.8 77.2% 49.8%' },
    dark:  { '--primary': '346.8 77.2% 49.8%', '--primary-foreground': '355.7 100% 97.3%', '--ring': '346.8 77.2% 49.8%' },
  },
  amber: {
    light: { '--primary': '37.7 92.1% 50.2%', '--primary-foreground': '26 83.3% 14.1%', '--ring': '37.7 92.1% 50.2%' },
    dark:  { '--primary': '43.3 96.4% 56.3%', '--primary-foreground': '26 83.3% 14.1%', '--ring': '43.3 96.4% 56.3%' },
  },
  cyan: {
    light: { '--primary': '189.5 94.5% 42.7%', '--primary-foreground': '210 40% 98%', '--ring': '189.5 94.5% 42.7%' },
    dark:  { '--primary': '187.9 85.7% 53.3%', '--primary-foreground': '198 100% 9.4%', '--ring': '187.9 85.7% 53.3%' },
  },
};

function getStoredTheme(): Theme {
  const stored = localStorage.getItem('isp_theme');
  if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  return 'system';
}

function getStoredAccent(): AccentColor {
  const stored = localStorage.getItem('isp_accent');
  if (stored && stored in ACCENT_PRESETS) return stored as AccentColor;
  return 'blue';
}

function isDarkMode(theme: Theme): boolean {
  return (
    theme === 'dark' ||
    (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)
  );
}

function applyTheme(theme: Theme) {
  const root = document.documentElement;
  root.classList.toggle('dark', isDarkMode(theme));
}

function applyAccent(accent: AccentColor, theme: Theme) {
  const root = document.documentElement;
  const mode = isDarkMode(theme) ? 'dark' : 'light';
  const vars = ACCENT_PRESETS[accent][mode];
  for (const [prop, val] of Object.entries(vars)) {
    root.style.setProperty(prop, val);
  }
}

export const useThemeStore = create<ThemeState>((set, get) => {
  // Apply initial theme + accent on store creation
  const initialTheme = getStoredTheme();
  const initialAccent = getStoredAccent();
  applyTheme(initialTheme);
  applyAccent(initialAccent, initialTheme);

  // Listen for system theme changes
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    const { theme, accentColor } = get();
    if (theme === 'system') {
      applyTheme('system');
      applyAccent(accentColor, 'system');
    }
  });

  return {
    theme: initialTheme,
    accentColor: initialAccent,

    setTheme: (theme) => {
      localStorage.setItem('isp_theme', theme);
      applyTheme(theme);
      applyAccent(get().accentColor, theme);
      set({ theme });
    },

    setAccentColor: (color) => {
      localStorage.setItem('isp_accent', color);
      applyAccent(color, get().theme);
      set({ accentColor: color });
    },

    toggleTheme: () => {
      const current = get().theme;
      const next: Theme = current === 'light' ? 'dark' : current === 'dark' ? 'system' : 'light';
      localStorage.setItem('isp_theme', next);
      applyTheme(next);
      applyAccent(get().accentColor, next);
      set({ theme: next });
    },
  };
});

export { ACCENT_PRESETS };
export type { Theme, AccentColor };
